'use client';

/**
 * Dependency imports
 */
import React from 'react';
import {
  Box,
  Container,
  Group,
  Loader,
  Stack,
  Text,
  TextInput,
  Modal,
  Button as MantineButton,
  ThemeIcon,
} from '@mantine/core';
import { loadStripe } from '@stripe/stripe-js';
import { motion } from 'framer-motion';
import { useForm } from '@mantine/form';
import { useAuthContext, AuthProvider } from '@/context/Auth';
import { useProfileContext, ProfileContextProvider } from '@/context/Profile';
import { isUserBuyer } from '@/utils/profile';
import BuyerVerificationModal from '@/components/pricing/BuyerVerificationModal';
import { notifications } from '@mantine/notifications';

/**
 * Local imports
 */
import CyclePicker from '@/components/pricing/CyclePicker';
import PricingPlan from '@/components/pricing/PricingPlan';
import SiteHeader from '@/components/SiteHeader';
import PageTitle from '@/components/PageTitle';
import FAQSection from '@/components/FAQSection';
import MoreFAQSection from '@/components/MoreFAQSection';
import HelpChangeSection from '@/components/HelpChangeSection';
import SiteFooter from '@/components/SiteFooter';
import Clients from '@/components/Clients';
import Testimonials from '@/components/Testimonials';
import Button from '@/components/Button';
import { useRender } from '@/hooks/use-render';
import { useResponsive } from '@/hooks/use-responsive';
import TestimonialsSection from '@/components/landing/GoogleReview';
import { getAuth } from 'firebase/auth';
import FAIcon from '@/components/FAIcon';

if (!process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY)
  // console.log('STRIPE_PUBLISHABLE_KEY not set');

  loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!);

const TierWaitlist = ({ tier }: { tier: string }) => {
  const inputFieldRef = React.useRef<HTMLInputElement>(null);
  const [active, setActive] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [error, setError] = React.useState('');
  const [modalOpened, setModalOpened] = React.useState(false);
  const [modalContent, setModalContent] = React.useState<{
    title: string;
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const form = useForm({
    initialValues: {
      email: '',
    },

    validate: {
      email: (value: string) => {
        if (!value) return 'Please enter your email';
        if (!/^\S+@\S+$/.test(value)) return 'Invalid email';
      },
    },
  });

  const handleFormSubmit = async (values: { email: string }) => {
    setSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/wishlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: values.email,
          service: `pricing-${tier.toLowerCase()}`
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to join wishlist');
      }

      if (data.isExisting) {
        setModalContent({
          title: 'Already on Waitlist',
          message: `You're already on the waitlist for the ${tier} plan!`,
          type: 'error'
        });
        setModalOpened(true);
        return;
      }

      setSubmitted(true);
      setModalContent({
        title: 'Success!',
        message: `You've been added to the ${tier} plan waitlist. We'll notify you when it's available.`,
        type: 'success'
      });
      setModalOpened(true);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred. Please try again later.');
      setModalContent({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to join wishlist',
        type: 'error'
      });
      setModalOpened(true);
    } finally {
      setSubmitting(false);
    }
  };

  React.useEffect(() => {
    if (active) {
      inputFieldRef.current?.focus();
    }
  }, [active]);

  if (submitted) {
    return (
      <Text fz={14} pt={5}>
        <strong>Thank you!</strong> We will notify you when this plan is
        available.
      </Text>
    );
  }

  return (
    <>
      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        centered
        withCloseButton
        title={
          <Group gap="sm">
            <ThemeIcon
              size="lg"
              radius="xl"
              variant="light"
              color={modalContent?.type === 'success' ? 'green' : 'red'}
            >
              <FAIcon
                name={modalContent?.type === 'success' ? 'check-circle' : 'exclamation-circle'}
                size={20}
              />
            </ThemeIcon>
            <Text fw={600} size="lg">
              {modalContent?.title}
            </Text>
          </Group>
        }
        styles={{
          title: {
            marginBottom: '1rem',
          },
          header: {
            marginBottom: '1rem',
          },
          body: {
            padding: '1.5rem',
          },
        }}
      >
        <Stack align="center" gap="md">
          <Text size="md" ta="center" c="dimmed">
            {modalContent?.message}
          </Text>
          {modalContent?.type === 'success' && (
            <Group gap="xs" c="green">
              <FAIcon name="envelope" size={16} />
              <Text size="sm">We &apos;ll send you an email confirmation shortly.</Text>
            </Group>
          )}
          <Group justify="flex-end" w="100%" mt="md">
            <MantineButton
              variant="light"
              onClick={() => setModalOpened(false)}
              color={modalContent?.type === 'success' ? 'green' : 'red'}
              leftSection={
                <FAIcon
                  name={modalContent?.type === 'success' ? 'check' : 'xmark'}
                  size={14}
                />
              }
            >
              {modalContent?.type === 'success' ? 'Got it!' : 'Close'}
            </MantineButton>
          </Group>
        </Stack>
      </Modal>

      <motion.div
        style={{ position: 'relative', textAlign: 'center' }}
        initial={{
          y: 0,
        }}
        animate={{
          y: active ? -15 : 0,
        }}
        transition={{
          duration: 0.2,
        }}
      >
        <motion.div
          style={{
            pointerEvents: active ? 'none' : 'all',
          }}
          animate={{
            opacity: active ? 0 : 1,
          }}
        >
          <Button
            variant="outline"
            color="dark"
            component="button"
            fullwidth
            transition={false}
            onClick={() => setActive(true)}
            expandOnHover={false}
          >
            Get notified
          </Button>
        </motion.div>

        <motion.form
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            pointerEvents: active ? 'all' : 'none',
            opacity: 0,
          }}
          animate={{
            opacity: active ? 1 : 0,
          }}
          transition={{
            opacity: {
              duration: 0.1,
              ease: 'linear',
            },
          }}
          onSubmit={form.onSubmit(handleFormSubmit)}
        >
          <TextInput
            ref={inputFieldRef}
            w="100%"
            mb={10}
            placeholder="Enter your email"
            size="lg"
            disabled={submitting}
            radius={99}
            error={error}
            {...form.getInputProps('email')}
            styles={{
              input: {
                fontSize: 14,
                height: 52,
                lineHeight: 52,
              },
            }}
          />

          <motion.div
            style={{
              width: '50%',
              margin: '0 auto',
              pointerEvents: submitting ? 'none' : 'all',
            }}
            animate={{
              width: submitting ? 34 : '50%',
            }}
            transition={{
              duration: 0.1,
            }}
          >
            <Button
              component="button"
              size="small"
              fullwidth
              disabled={submitting}
              transition={false}
              style={{ paddingRight: 0, paddingLeft: 0 }}
              expandOnHover={false}
            >
              {submitting ? <><Loader size="xs" /></> : 'Submit'}
            </Button>
          </motion.div>
        </motion.form>
      </motion.div>
    </>
  );
};

const AuthenticatedContent = () => {
  const { userRole } = useProfileContext();
  const [showBuyerVerification, setShowBuyerVerification] = React.useState(false);
  const [docReviewLoading, setDocReviewLoading] = React.useState(false);

  const handlePricingClick = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isUserBuyer(userRole)) {
      // If user is not a buyer, show verification modal
      setShowBuyerVerification(true);
      return;
    }

    // If user is a buyer, proceed with payment
    setDocReviewLoading(true);
    try {
      const form = e.target as HTMLFormElement;
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        return null
      }

      const token = await user.getIdToken();

      const response = await fetch('/api/payment/create', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          product_type: 'doc_review',
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create checkout session');
      }

      // If successful, redirect to Stripe checkout
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to process payment',
        color: 'red'
      });
    } finally {
      setDocReviewLoading(false);
    }
  };

  return (
    <>
      <BuyerVerificationModal
        opened={showBuyerVerification}
        onClose={() => setShowBuyerVerification(false)}
      />
      <form onSubmit={handlePricingClick}>
        <input name="product_type" type="hidden" value="doc_review" />
        <Button
          component="button"
          variant="filled"
          color="dark"
          fullwidth
          transition={false}
          disabled={docReviewLoading}
          expandOnHover={false}
        >
          {docReviewLoading ? <Loader size={25} /> : 'Get Started'}
        </Button>
      </form>
    </>
  );
};

const PricingPageContent = () => {
  const { responsive } = useResponsive();
  const render = useRender({ smoothing: true });
  const [cycle, setCycle] = React.useState<'monthly' | 'yearly'>('yearly');
  const { state: { isVerified }, dispatch } = useAuthContext();

  const handlePricingClick = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isVerified) {
      dispatch({ type: 'OPEN_MODAL', payload: { defaultTab: 'login' } });
    }
  };

  return render(
    // Fixed content
    <SiteHeader scheme="dark" />,

    // Page content
    <main>
      <PageTitle
        subtitle="Plans & Pricing"
        title="Scale as you grow"
        tagline="Find the right plan for your needs"
        pt="25vh"
        pb="7vh"
        bg="gray.0"
      />

      <Box ta="center" bg="gray.0" pb={200} mb={-112}>
        <CyclePicker onChange={setCycle} />
      </Box>

      <Container size="xl" pb={100}>
        <Group
          justify="space-between"
          align={responsive({
            xs: 'stretch',
            sm: 'center',
            xl: 'stretch',
          })}
          wrap={responsive({
            xs: 'wrap',
            xl: 'nowrap',
          })}
          style={{
            flexDirection: responsive({
              xs: 'column',
              xl: 'row',
            }),
          }}
        >
          <Group
            gap={15}
            align="stretch"
            wrap={responsive({
              xs: 'wrap',
              md: 'nowrap',
            })}
            justify="center"
            style={{
              flexDirection: responsive({
                xs: 'column',
                lg: 'row',
              }),
            }}
          >
            <PricingPlan
              w={responsive({
                xs: '100%',
                sm: 360,
                lg: 280,
              })}
              pb={80}
              name="Basic"
              description="Individuals / Very Small Teams"
              price={0}
              callToAction={<TierWaitlist tier="Free" />}
              features={[
                <Text key={1} fz={14}>
                  Document storage
                </Text>,
                <Text key={2} fz={14}>
                  Bylaw Chat 2/day/month
                </Text>
              ]}
            />

            <PricingPlan
              w={responsive({
                xs: '100%',
                sm: 360,
                lg: 280,
              })}
              pb={80}
              variant="featured"
              name="Tier1"
              description="Small Teams & Professionals"
              price={cycle === 'monthly' ? 7 : 67}
              cycle={cycle}
              callToAction={<TierWaitlist tier="Tier1" />}
              features={[
                <Text key={1} fz={14}>
                  Store and share
                </Text>,
                <Text key={2} fz={14}>
                  Bylaw Chat 10/day/month
                </Text>
              ]}
            />

            <PricingPlan
              w={responsive({
                xs: '100%',
                sm: 360,
                lg: 280,
              })}
              pb={80}
              name="Tier2"
              description="Large Teams & Advanced Needs"
              price={cycle === 'monthly' ? 15 : 135}
              cycle={cycle}
              callToAction={<TierWaitlist tier="Tier2" />}
              features={[
                <Text key={1} fz={14}>
                  Bylaws Chat Unlimited
                </Text>
              ]}
            />
          </Group>

          {/* Document Review Plan */}
          <PricingPlan
            pb={80}
            mt={responsive({
              xs: 100,
              xl: 0,
            })}
            w={responsive({
              xs: '100%',
              sm: 460,
              xl: 280,
            })}
            variant="inverted"
            name="Document Review"
            description="Comprehensive Review Service"
            price={390}
            cycle="Doc Review"
            callToAction={
              isVerified ? (
                <ProfileContextProvider>
                  <AuthenticatedContent />
                </ProfileContextProvider>
              ) : (
                <form onSubmit={handlePricingClick}>
                  <input name="product_type" type="hidden" value="doc_review" />
                  <Button
                    component="button"
                    variant="filled"
                    color="dark"
                    fullwidth
                    transition={false}
                    expandOnHover={false}
                  >
                    Get Started
                  </Button>
                </form>
              )
            }
            features={[
              <Text key={1} fz={14}>
                24 Hour turn around
              </Text>,
              <Text key={2} fz={14}>
                Evening/Weekend work
              </Text>,
              <Text key={3} fz={14}>
                Status updates
              </Text>,
              <Text key={4} fz={14}>
                Online dashboard
              </Text>,
              <Text key={5} fz={14}>
                Interactive review
              </Text>,
              <Group key={7} gap={5}>
                <Text fz={14}>Tier 2 Subscription</Text>
                <Text fz={12} c="gray">
                  (3 months)
                </Text>
              </Group>,
            ]}
          />
        </Group>
      </Container>

      <Clients />
      <TestimonialsSection />
      <FAQSection />
      <MoreFAQSection />
      <HelpChangeSection />
      <SiteFooter />
    </main>
  );
};

// Wrap with AuthProvider only
const PricingPage = () => {
  return (
    <AuthProvider>
      <PricingPageContent />
    </AuthProvider>
  );
};

export default PricingPage;
